import { useCallback, useMemo, useState, useTransition } from "react";
import qs from "query-string";
import { useEffect, useRef } from "react";
import styled, { css } from "styled-components/macro";
import { AppBar, Tabs, Tab, useMediaQuery } from "@mui/material";
import {
  FONT_SIZE_MEDIUM,
  BREAKPOINT_MOBILE,
  BREAKPOINT_TABLET,
} from "../../constants/constants";
import {
  darkBlue,
  darkModeBackgroundHoveredColor,
  darkModeTabIndicatorColor,
  darkModeTextColor,
  lightGrey,
  medBlue,
} from "../../constants/colors";
import { useLocation, useNavigate } from "react-router";
import { removeKeysFromObject } from "utils/arrayUtils";
import { useCardMargins } from "App/useCardMargins";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useIsDarkMode } from "store/atoms";

export const TILES_TIMELINE_TABS_HEIGHT = 54;

const TILES_TAB_INDEX = 0;
const TIMELINE_TAB_INDEX = 1;
const TILES_SEARCH_VALUE = "";
const TL = "tl";

const TabStyles = styled.div<{
  $isDarkMode: boolean;
  $isPending: boolean;
  $cardMarginOuter: number;
  $cardMarginInner: number;
}>`
  margin: 0 ${props => props.$cardMarginOuter * 3}px -${props => 2 * props.$cardMarginInner}px;
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    margin: 0 ${props => props.$cardMarginOuter * 3}px -${props => props.$cardMarginInner}px;
  }
  ${props =>
    props.$isPending &&
    css`
      /* opacity: 0.5; */
      cursor: wait;
    `}
  .appBar {
    height: ${TILES_TIMELINE_TABS_HEIGHT}px;
    width: 100%;
  }
  .MuiTabs-root {
    height: 100%;
  }
  .MuiPaper-root {
    background-color: rgba(0, 0, 0, 0);
  }
  .MuiTabs-indicator {
    background-color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeTabIndicatorColor : darkBlue};
    height: 5px;
  }
  .MuiTouchRipple-ripple {
    background-color: ${medBlue};
  }
  @media (min-width: 600px) {
    .MuiTab-root {
      min-width: 160px;
    }
  }
  button {
    color: ${({ $isDarkMode }) => ($isDarkMode ? darkModeTextColor : darkBlue)};
    font-size: ${FONT_SIZE_MEDIUM}px;
    &.Mui-selected {
      color: ${({ $isDarkMode }) =>
        $isDarkMode ? darkModeTextColor : darkBlue};
    }
  }
  .tab {
    text-transform: initial;
    &:hover {
      background: ${({ $isDarkMode }) =>
        $isDarkMode ? darkModeBackgroundHoveredColor : lightGrey};
    }
  }
  .Mui-selected {
    pointer-events: none;
  }
  .MuiButtonBase-root:not(.Mui-selected) {
    opacity: 0.75;
  }

  @media print {
    width: 100%;

    .MuiTabs-indicator {
      left: 610px !important;
    }
  }
`;

export const ViewTimelineTabs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => qs.parse(location.search), [location.search]);
  const currentView = useMemo(
    () =>
      query.view && query.view === "tl"
        ? 1 // Timeline
        : 0,
    [query.view]
  );
  const [view, setView] = useState(currentView);
  const [isPending, startTransition] = useTransition();

  // switch between timeline and not-timeline
  const handleChangeView = useCallback(
    (event, value) => {
      startTransition(() => {
        setView(value); // optional: toggle can be faster without animation if we disable this timeout

        const shouldBeTilesPage = value === 0;
        const nextQueryObj = shouldBeTilesPage
          ? removeKeysFromObject(query, ["view"])
          : { ...query, view: "tl" };
        const queryStringified = qs.stringify(nextQueryObj as any);
        setTimeout(() => {
          navigate(
            location.pathname + (queryStringified ? `?${queryStringified}` : "")
          );
        }, 100);
      });
    },
    [query, location.pathname, navigate, startTransition]
  );

  // ensure we're always synchronized to the page
  const prevSearchRef = useRef(null as string | null);
  useEffect(() => {
    if (location.search !== prevSearchRef.current) {
      prevSearchRef.current = location.search;
      const shouldBeTimelinePage = "view" in query && query.view === TL;
      startTransition(() => {
        if (
          location.search === TILES_SEARCH_VALUE &&
          view !== TILES_TAB_INDEX
        ) {
          setView(TILES_TAB_INDEX);
        } else if (shouldBeTimelinePage && view !== TIMELINE_TAB_INDEX) {
          setView(TIMELINE_TAB_INDEX);
        }
      });
    }
  }, [location, view, setView, query]);
  const isAboveMobile = useMediaQuery(`(min-width: ${BREAKPOINT_MOBILE}px)`);

  const { cardMarginInner, cardMarginOuter } = useCardMargins();
  const { translate } = useFlightTranslation();
  const [isDarkMode] = useIsDarkMode();
  return (
    <TabStyles
      $cardMarginOuter={cardMarginOuter}
      $cardMarginInner={cardMarginInner}
      data-component-name={"Tiles/Timeline Tabs"}
      className={`doNotPrint${view ? " active" : ""}`}
      $isDarkMode={isDarkMode}
      $isPending={isPending}
    >
      <AppBar className="appBar" position="static" elevation={0}>
        <Tabs
          centered={true}
          value={view}
          onChange={handleChangeView}
          indicatorColor="primary"
          variant={isAboveMobile ? "standard" : "fullWidth"}
        >
          <Tab
            className="tab processTilesTab"
            label={translate("Tiles")}
            value={TILES_TAB_INDEX}
          />
          <Tab
            className="tab timelineTab"
            label={translate("Timeline")}
            value={TIMELINE_TAB_INDEX}
          />
        </Tabs>
      </AppBar>
    </TabStyles>
  );
};
export default ViewTimelineTabs;
